/* ============================================================
   GD Events Feed — reads $app:event occurrence metaobjects from the shop's
   own Storefront API and adapts them to the BinderPOS event shape the theme
   was built against. Pure helpers are unit-tested (test/events-feed.test.mjs);
   config = { token, type, apiVersion } comes from window.theme.eventsFeed.
   ============================================================ */
(function (root, factory) {
  var api = factory();
  if (typeof module === "object" && module.exports) {
    module.exports = api;
  } else {
    root.GDEventsFeed = api;
  }
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  var EVENTS_QUERY =
    "query GDEvents($type: String!, $cursor: String) {" +
    " metaobjects(type: $type, first: 250, after: $cursor) {" +
    "  pageInfo { hasNextPage endCursor }" +
    "  nodes { id fields { key value reference {" +
    "   __typename" +
    "   ... on Product { id handle }" +
    "   ... on MediaImage { image { url } }" +
    "   ... on GenericFile { url }" +
    "  } } }" +
    " } }";

  function fieldMap(node) {
    var map = {};
    (node.fields || []).forEach(function (field) {
      map[field.key] = field; // last write wins for duplicated keys
    });
    return map;
  }

  function referenceUrl(field) {
    if (!field || !field.reference) return null;
    if (field.reference.image && field.reference.image.url) return field.reference.image.url;
    if (field.reference.url) return field.reference.url;
    return null;
  }

  function adaptMetaobjectNode(node) {
    var fields = fieldMap(node);
    var value = function (key) { return fields[key] ? (fields[key].value || "") : ""; };
    if (value("occurrence_status") === "CANCELLED") return null;

    var startAt = value("start_at");
    var datePart = startAt.split("T")[0] || "";
    var timePart = (startAt.split("T")[1] || "00:00:00").slice(0, 8);
    if (timePart.length === 5) timePart += ":00";

    var product = fields.ticket_product && fields.ticket_product.reference;
    var ticketed = !!(product && product.handle);
    var banner = referenceUrl(fields.hero_image);
    var ticketImage = referenceUrl(fields.ticket_image) || banner;

    var additionalInfo = [];
    try {
      var parsed = JSON.parse(value("registration_questions") || "[]");
      if (Array.isArray(parsed)) additionalInfo = parsed;
    } catch (e) { /* default [] */ }

    return {
      id: parseInt((node.id || "").split("/").pop(), 10),
      title: value("title"),
      date: datePart + "T00:00Z[GMT]",
      time: timePart,
      game: value("game"),
      type: value("event_type"),
      description: value("description"),
      prizeStructure: value("prize_structure"),
      buildingName: value("venue_name"),
      streetAddress: value("street_address"),
      city: value("city"),
      state: value("state"),
      zipCode: value("zip_code"),
      banner: banner,
      ticketImage: ticketImage,
      calendarIcon: referenceUrl(fields.calendar_icon) || banner,
      ticketed: ticketed,
      isTicketed: ticketed,
      handle: ticketed ? product.handle : null,
      additionalInfo: additionalInfo,
    };
  }

  function filterEventsByRange(events, startDate, endDate) {
    return events.filter(function (event) {
      var day = (event.date || "").split("T")[0];
      return day >= startDate && day <= endDate;
    });
  }

  var cache = null;
  var inFlight = null;

  function fetchAllEvents(config) {
    if (!config || !config.token || !config.type) {
      return Promise.reject(new Error("events feed not configured"));
    }
    if (cache) return Promise.resolve(cache);
    if (inFlight) return inFlight;

    var endpoint = "/api/" + (config.apiVersion || "2026-04") + "/graphql.json";
    var collected = [];

    function page(cursor) {
      return fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": config.token,
        },
        body: JSON.stringify({ query: EVENTS_QUERY, variables: { type: config.type, cursor: cursor } }),
      })
        .then(function (response) {
          if (!response.ok) throw new Error("events feed request failed: " + response.status);
          return response.json();
        })
        .then(function (json) {
          if (json.errors && json.errors.length) {
            throw new Error("events feed error: " + json.errors[0].message);
          }
          var connection = json.data.metaobjects;
          connection.nodes.forEach(function (node) {
            var event = adaptMetaobjectNode(node);
            if (event) collected.push(event);
          });
          if (connection.pageInfo.hasNextPage) return page(connection.pageInfo.endCursor);
          return collected;
        });
    }

    inFlight = page(null)
      .then(function (events) {
        cache = events;
        inFlight = null;
        return events;
      })
      .catch(function (error) {
        inFlight = null;
        throw error;
      });
    return inFlight;
  }

  function getEvents(config, startDate, endDate) {
    return fetchAllEvents(config).then(function (events) {
      return filterEventsByRange(events, startDate, endDate);
    });
  }

  return {
    EVENTS_QUERY: EVENTS_QUERY,
    adaptMetaobjectNode: adaptMetaobjectNode,
    filterEventsByRange: filterEventsByRange,
    fetchAllEvents: fetchAllEvents,
    getEvents: getEvents,
  };
});
